import { useEffect, useState } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { PublicClientApplication } from "@azure/msal-browser";
import { encryptTokenForTeamsAuth } from "../../api";

export function AuthEnd() {
  const [status, setStatus] = useState<"processing" | "success" | "error">("processing");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const completeAuth = async () => {
      try {
        await microsoftTeams.app.initialize();

        const hash = window.location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);

        const authError = hashParams.get("error");
        if (authError) {
          throw new Error(hashParams.get("error_description") || authError);
        }

        const configStr = localStorage.getItem("teams.auth.config");
        if (!configStr) {
          throw new Error("Missing auth configuration");
        }

        const { clientId, authority } = JSON.parse(configStr);
        localStorage.removeItem("teams.auth.config");

        const msalInstance = new PublicClientApplication({
          auth: {
            clientId,
            authority,
            redirectUri: `${window.location.origin}/auth-end`,
            knownAuthorities: [new URL(authority).hostname],
            navigateToLoginRequestUrl: false,
          },
          cache: {
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false,
          },
          system: {
            allowRedirectInIframe: true,
          },
        });

        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();

        if (!response?.account) {
          throw new Error("No account received from B2C");
        }

        // Encrypt the access token server-side (Fernet/AES) so it is unreadable to a
        // malicious Teams opener that might intercept the notifySuccess payload.
        if (!response.accessToken) {
          throw new Error("No access token received from B2C");
        }
        const encryptedToken = await encryptTokenForTeamsAuth({
          accessToken: response.accessToken,
        });

        // Build auth result with encrypted token and plain account info
        const authResult = {
          account: {
            homeAccountId: response.account.homeAccountId,
            environment: response.account.environment,
            tenantId: response.account.tenantId,
            username: response.account.username,
            localAccountId: response.account.localAccountId,
            name: response.account.name,
            idTokenClaims: response.account.idTokenClaims,
          },
          encryptedToken,
          accessTokenExpiry: response.expiresOn ? Math.floor(response.expiresOn.getTime() / 1000) : null,
          scopes: response.scopes,
        };

        // Pass as JSON - the server-signed wrapper allows the backend to verify integrity
        setStatus("success");
        microsoftTeams.authentication.notifySuccess(JSON.stringify(authResult));

      } catch (err) {
        console.error("AuthEnd error:", err);
        const message = err instanceof Error ? err.message : "Authentication failed";
        setStatus("error");
        setError(message);
        
        localStorage.removeItem("teams.auth.config");
        microsoftTeams.authentication.notifyFailure(message);
      }
    };

    completeAuth();
  }, []);

  return (
    <div style={{ padding: "20px", textAlign: "center" }}>
      {status === "processing" && <p>Completing sign-in...</p>}
      {status === "success" && <p>Success! This window will close.</p>}
      {status === "error" && (
        <div>
          <p style={{ color: "red" }}>Sign-in failed</p>
          {error && <p style={{ fontSize: "14px" }}>{error}</p>}
        </div>
      )}
    </div>
  );
}

export default AuthEnd;